import { Link } from "react-router-dom";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronDown } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";

export interface MegaMenuItem {
  to: string;
  label: string;
  description?: string;
  badge?: string;
}

interface MegaMenuItemsProps {
  label: string;
  items: MegaMenuItem[];
  /** Optional footer link, e.g. "Compare all →". */
  footer?: { to: string; label: string };
  /** When true, renders a 2px terracotta active-route underline beneath the trigger. */
  active?: boolean;
}

/**
 * Shared state across all MegaMenu siblings inside a MegaMenuRoot.
 *
 * Why centralised: previously each MegaMenu owned its own open boolean and
 * its own 120ms close-timer. When the cursor moved from one trigger to the
 * next, the leaving menu kept its panel open for 120ms while the entering
 * menu opened immediately — so for a beat both were mounted, both panels
 * animated (zoom-in/zoom-out), and Radix focus management bounced between
 * them. Effects: visible jitter, triggers stealing focus, and the cursor
 * sometimes landing on a panel that was already closing.
 *
 * With shared state, only one menu is open at any moment, switching is
 * instantaneous (no animation overlap), and the close-timer is a single
 * cursor-leaves-the-nav guard rather than per-menu.
 */
type MegaMenuContextValue = {
  openId: string | null;
  open: (id: string) => void;
  scheduleCloseAll: () => void;
  cancelClose: () => void;
  closeAll: () => void;
};

const MegaMenuContext = createContext<MegaMenuContextValue | null>(null);

const useMegaMenuContext = () => useContext(MegaMenuContext);

/**
 * A single mega-menu trigger + content. Each menu uses its own Radix Popover
 * for correct anchoring under its own trigger, but the open/close lifecycle
 * is coordinated by MegaMenuRoot so only one panel is ever mounted at once.
 */
export const MegaMenu = ({ label, items, footer, active = false }: MegaMenuItemsProps) => {
  const ctx = useMegaMenuContext();
  const id = useId();

  // Fallback for tests / standalone usage where the root context isn't present.
  const [localOpen, setLocalOpen] = useState(false);
  const localTimer = useRef<number | null>(null);
  const cancelLocal = useCallback(() => {
    if (localTimer.current !== null) {
      window.clearTimeout(localTimer.current);
      localTimer.current = null;
    }
  }, []);
  const scheduleLocalClose = useCallback(() => {
    cancelLocal();
    localTimer.current = window.setTimeout(() => setLocalOpen(false), 60);
  }, [cancelLocal]);
  useEffect(() => () => cancelLocal(), [cancelLocal]);

  const open = ctx ? ctx.openId === id : localOpen;
  const handleOpen = () => (ctx ? ctx.open(id) : setLocalOpen(true));
  const handleClose = () => (ctx ? ctx.scheduleCloseAll() : scheduleLocalClose());
  const handleCancelClose = () => (ctx ? ctx.cancelClose() : cancelLocal());
  const handleCloseNow = () => (ctx ? ctx.closeAll() : setLocalOpen(false));

  // Radix calls onOpenChange when it wants to close (escape, outside click, etc.).
  // We forward false → closeAll; we ignore true because we drive opens explicitly
  // from hover/focus to keep the shared state authoritative.
  const handleOpenChange = (next: boolean) => {
    if (!next) handleCloseNow();
  };

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          onMouseEnter={() => {
            handleCancelClose();
            handleOpen();
          }}
          // Note: no onMouseLeave here. Closing is driven by the root wrapper's
          // onMouseLeave (cursor leaves the whole nav strip) so moving between
          // triggers across the gap never schedules a close that could race
          // with the next trigger's open.
          aria-current={active ? "page" : undefined}
          className={`group relative inline-flex items-center gap-1 h-9 px-1
            text-[13px] font-medium tracking-[-0.01em]
            ${active ? "text-[hsl(var(--fg-cream))]" : "text-[hsl(var(--fg-cream))]/80"}
            hover:text-[hsl(var(--fg-cream))]
            data-[state=open]:text-[hsl(var(--fg-cream))]
            transition-colors duration-100 ease-out
            outline-none focus:outline-none focus-visible:outline-none bg-transparent`}
        >
          {label}
          <ChevronDown
            className="h-3 w-3 transition-transform duration-100 group-data-[state=open]:rotate-180"
            aria-hidden="true"
          />
          {active && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[hsl(var(--accent-brick))]"
            />
          )}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={8}
          collisionPadding={12}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={handleCancelClose}
          onMouseLeave={handleClose}
          // No data-state animation classes here. Radix's Presence component
          // waits for an animationend event before unmounting Popover.Content
          // when data-[state=closed]:animate-out is present. With tailwindcss-
          // animate's default 0ms duration, that event can fire late or never,
          // leaving the previous panel in the DOM after switching menus —
          // exactly the "two panels open at once" symptom we hit. Mounting is
          // now driven purely by the `open` prop, which our shared state
          // guarantees is exclusive across siblings.
          className="z-[120] min-w-[280px] p-2 rounded-md
            bg-[hsl(var(--panel))] border border-[hsl(var(--border-hairline))]
            shadow-2xl"
        >
          <ul className="grid gap-0.5 m-0 p-0 list-none">
            {items.map(({ to, label: itemLabel, description, badge }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={handleCloseNow}
                  className="group block rounded-sm px-3 py-2.5 no-underline
                    text-[hsl(var(--fg-cream))]/80 hover:text-[hsl(var(--fg-cream))]
                    hover:bg-white/[0.04] transition-colors
                    focus:outline-none focus-visible:bg-white/[0.06]
                    focus-visible:text-[hsl(var(--fg-cream))]"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-medium leading-none">
                      {itemLabel}
                    </span>
                    {badge && (
                      <span
                        className="text-[9px] font-mono uppercase tracking-[0.1em]
                          px-1.5 py-0.5 rounded-sm
                          bg-[hsl(var(--accent-brick))]/15
                          text-[hsl(var(--accent-brick-hover))]
                          border border-[hsl(var(--accent-brick))]/30"
                      >
                        {badge}
                      </span>
                    )}
                  </div>
                  {description && (
                    <p className="mt-1 text-[11px] leading-snug text-[hsl(var(--fg-muted))] m-0">
                      {description}
                    </p>
                  )}
                </Link>
              </li>
            ))}
            {footer && (
              <>
                <li className="my-1 h-px bg-[hsl(var(--border-hairline))]" aria-hidden="true" />
                <li>
                  <Link
                    to={footer.to}
                    onClick={handleCloseNow}
                    className="block rounded-sm px-3 py-2 no-underline
                      text-[11px] font-mono uppercase tracking-[0.1em]
                      text-[hsl(var(--accent-brick-hover))] hover:text-[hsl(var(--fg-cream))]
                      hover:bg-white/[0.04] transition-colors
                      focus:outline-none focus-visible:bg-white/[0.06]"
                  >
                    {footer.label}
                  </Link>
                </li>
              </>
            )}
          </ul>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

/**
 * Layout wrapper + shared open-state coordinator. Renders triggers in a row
 * and ensures only one MegaMenu panel is open at a time. The single shared
 * close-timer fires on cursor-leaves-nav, not per-menu, so moving across
 * triggers swaps panels instantly without animation overlap or focus jitter.
 */
export const MegaMenuRoot = ({ children }: { children: React.ReactNode }) => {
  const [openId, setOpenId] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const open = useCallback(
    (id: string) => {
      cancelClose();
      setOpenId(id);
    },
    [cancelClose],
  );

  const scheduleCloseAll = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpenId(null), 60);
  }, [cancelClose]);

  const closeAll = useCallback(() => {
    cancelClose();
    setOpenId(null);
  }, [cancelClose]);

  useEffect(() => () => cancelClose(), [cancelClose]);

  const value = useMemo<MegaMenuContextValue>(
    () => ({ openId, open, scheduleCloseAll, cancelClose, closeAll }),
    [openId, open, scheduleCloseAll, cancelClose, closeAll],
  );

  return (
    <MegaMenuContext.Provider value={value}>
      <div
        className="flex items-center gap-7"
        onMouseLeave={scheduleCloseAll}
        onMouseEnter={cancelClose}
      >
        {children}
      </div>
    </MegaMenuContext.Provider>
  );
};
